import { useCallback, useEffect, useLayoutEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import { ContentDropdown } from './ContentDropdown/ContentDropdown'
import { ContentInput } from './ContentInput/ContentInput'
import { ContentDatepicker } from './ContentDatepicker/ContentDatepicker'
import { ContentTagInput } from './ContentTagInput/ContentTagInput'

import './ContentEditable.css'

const ContentEditable = forwardRef(({ 
    type, 
    value, 
    options, 
    onBlur, 
    onEditEnd, 
    placeholder, 
    grow, 
    disabled, 
    allowsNewLine = false, 
    shouldBlurOnEnter = true,
    alignment,
    ...rest 
}, ref) => {
    const [editValue, setEditValue] = useState(value ?? '')
    const rootElement = useRef()
    
    useEffect(() => {
        setEditValue(value ?? '')
    }, [value])
    
    useLayoutEffect(() => {
        // @todo add some validations like if its a dropdown it needs "options" property
        
        if (rootElement.current) {
            // adds relative position 
            const parent = rootElement.current.parentNode
            
            parent.style.position = 'relative'
            
            // hacky fix to make the first children of the TD
            // to occupy 100% of its height - works only if the td has just a single child
            if (parent.tagName === 'TD' && parent.childNodes.length === 1)
                parent.style.height = '0'
            
        }
    }, [])
    
    const onContentEdited = useCallback((finalValue, element) => {
        const currVal = (value ?? '')
        const finalVal = typeof finalValue === 'string' ? finalValue.trim() : (finalValue ?? '')
        
        onBlur?.(finalValue, element)
        
        if (finalVal !== currVal) {
            onEditEnd(finalValue)
        }
    }, [onEditEnd, value])
    
    const onChange = useCallback(value => {
        // console.log('onChange value', value)
        setEditValue(value)
        onContentEdited(value)
    }, [onContentEdited])
    
    const renderInput = useCallback(() => {
        switch (type) {
            case 'text':
            case 'textarea': return (
                <ContentInput
                    type={type}
                    value={editValue}
                    onChange={setEditValue}
                    onBlur={onContentEdited}
                    placeholder={placeholder}
                    alignment={alignment}
                    grow={grow} 
                    allowsNewLine={allowsNewLine}
                    shouldBlurOnEnter={shouldBlurOnEnter}/>
            )
            case 'dropdown': return (
                <ContentDropdown
                    value={options.find(o => o.value === editValue)}
                    options={options}
                    onChange={onChange} />
            )
            case 'datepicker': return (
                <ContentDatepicker
                    value={editValue}
                    onChange={onChange}
                    placeholder={placeholder} />
            )
            case 'tag': return (
                <ContentTagInput
                    tags={editValue || []}
                    onChange={onChange}
                    placeholder={placeholder}
                    removeButtonText={rest.removeButtonText}
                    suggestions={rest.suggestions} />
            )
        }
    }, [
        editValue,
        grow,
        onChange,
        onContentEdited,
        options,
        placeholder,
        rest.removeButtonText,
        rest.suggestions,
        type,
    ])
    
    useImperativeHandle(ref, () => ({
        setEditValue,
        rootElement,
    }))
    
    return (
        <div className="content-editable" ref={rootElement}>
            {disabled ? (
                <div className="content-editable__textarea whitespace-pre-line">{editValue}</div>
            ) : (
                renderInput()
            )}
        </div>
    )
})

export default ContentEditable
